import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/returns.scss";

function Returns() {
  return (
    <main className="returns-page policy-page" id="main-content">
      <header className="policy-hero">
        <p className="policy-eyebrow">Customer care</p>
        <h1>Returns &amp; exchanges</h1>
        <p className="policy-updated">Last updated October 23, 2026</p>
        <p className="policy-intro">
          We hope every piece earns a place in your routine. If something is not
          right, eligible items may be returned within
          {" "}{siteConfig.shipping.returnsWindowDays} days of delivery.
        </p>
      </header>

      <div className="policy-layout">
        <aside className="policy-toc">
          <nav aria-label="On this page">
            <p>On this page</p>
            <ol>
              <li><a href="#returns-eligibility">Return eligibility</a></li>
              <li><a href="#returns-start">Start a return</a></li>
              <li><a href="#returns-refunds">Refunds</a></li>
              <li><a href="#returns-exchanges">Exchanges</a></li>
              <li><a href="#returns-damage">Damaged or incorrect items</a></li>
              <li><a href="#returns-exclusions">Final sale and exclusions</a></li>
              <li><a href="#returns-international">International returns</a></li>
              <li><a href="#returns-rights">Mandatory consumer rights</a></li>
            </ol>
          </nav>
        </aside>

        <article className="policy-content">
          <div className="policy-callout" aria-label="Return policy summary">
            <div>
              <strong>{siteConfig.shipping.returnsWindowDays} days</strong>
              <span>from delivery to request a return</span>
            </div>
            <div>
              <strong>Original condition</strong>
              <span>unused, unmarked, and complete</span>
            </div>
            <div>
              <strong>Order number</strong>
              <span>required to locate your purchase</span>
            </div>
          </div>

          <section id="returns-eligibility">
            <span className="policy-section-number">01</span>
            <h2>Return eligibility</h2>
            <p>
              A return request must be submitted within
              {" "}{siteConfig.shipping.returnsWindowDays} days after tracking
              shows delivery. Items must be unused, unmarked, and returned with
              their original packaging, inserts, and accessories. Planner covers
              should not be creased, and paper goods must remain clean and unpunched.
            </p>
            <p>
              Keep your dispatch email and order number. Purchases made through
              another retailer must be returned to that retailer under its policy.
            </p>
          </section>

          <section id="returns-start">
            <span className="policy-section-number">02</span>
            <h2>Start a return</h2>
            <ol className="policy-steps">
              <li>
                <strong>Request authorization.</strong>
                <span>
                  Use the <Link to="/contact">contact form</Link> and select
                  “Return or exchange.” Include your order number and the items.
                </span>
              </li>
              <li>
                <strong>Wait for instructions.</strong>
                <span>We will confirm eligibility and send the return address and packing guidance.</span>
              </li>
              <li>
                <strong>Pack and send.</strong>
                <span>Use protective packaging and a tracked service. Keep the receipt until your refund is complete.</span>
              </li>
            </ol>
            <p>
              Returns sent without authorization may be delayed or sent back.
              Unless an item arrived damaged or incorrect, return delivery costs
              are deducted from the refund or paid directly to the carrier.
            </p>
          </section>

          <section id="returns-refunds">
            <span className="policy-section-number">03</span>
            <h2>Refunds</h2>
            <p>
              We inspect returns after arrival and email you with the outcome.
              Approved refunds are sent to the original payment method, normally
              within 5–10 business days after inspection. Your bank may need
              additional time to post the credit.
            </p>
            <p>
              Original delivery fees, expedited upgrades, gift wrapping, duties,
              and taxes collected by another authority are not refundable unless
              required by law or the return resulted from our error.
            </p>
          </section>

          <section id="returns-exchanges">
            <span className="policy-section-number">04</span>
            <h2>Exchanges</h2>
            <p>
              Inventory moves quickly, so we cannot reserve replacement items.
              The fastest option is to return the original item and place a new
              order. If you need help checking compatibility or dimensions before
              reordering, our client care team will be glad to assist.
            </p>
          </section>

          <section id="returns-damage">
            <span className="policy-section-number">05</span>
            <h2>Damaged or incorrect items</h2>
            <p>
              Inspect your delivery promptly. Contact us within 7 days if an item
              is damaged, defective, missing, or different from what you ordered.
              Include the order number plus clear photos of the item, packaging,
              and shipping label so we can resolve it quickly.
            </p>
          </section>

          <section id="returns-exclusions">
            <span className="policy-section-number">06</span>
            <h2>Final sale and exclusions</h2>
            <p>
              Personalized items, opened dated inserts, gift cards, downloadable
              products, and merchandise clearly marked final sale cannot be
              returned unless faulty. Used, altered, stained, scented, or
              incomplete items are also ineligible.
            </p>
          </section>

          <section id="returns-international">
            <span className="policy-section-number">07</span>
            <h2>International returns</h2>
            <p>
              Customers returning items from outside {siteConfig.operatingRegion.country} are responsible for return postage and
              accurate carrier paperwork. Return delivery and local carrier fees
              are not reimbursed. Review our
              {" "}<Link to="/shipping">shipping information</Link> before sending
              the parcel, then retain tracking until it reaches us.
            </p>
          </section>

          <section id="returns-rights">
            <span className="policy-section-number">08</span>
            <h2>Mandatory consumer rights</h2>
            <p>
              This voluntary {siteConfig.shipping.returnsWindowDays}-day policy
              does not limit a remedy that cannot be excluded by law, including
              rights for goods that are faulty, unsafe, not as described, or not
              supplied. A final-sale label never removes those mandatory rights.
            </p>
            <p>
              Consumers in the EEA, United Kingdom, and some other locations may
              have a statutory right to withdraw from a distance purchase within
              a specified period, often 14 days after delivery, subject to legal
              exceptions. Where that right applies, notify us clearly within the
              legal period. We will provide the required return and refund process,
              including any treatment of standard outbound delivery charges.
            </p>
          </section>

          <nav className="policy-related" aria-label="Related policies">
            <p>More customer care</p>
            <div>
              <Link to="/shipping">Shipping information <span aria-hidden="true">&rarr;</span></Link>
              <Link to="/terms">Terms of service <span aria-hidden="true">&rarr;</span></Link>
              <Link to="/contact">Contact client care <span aria-hidden="true">&rarr;</span></Link>
            </div>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Returns;
